// components/GST.jsx
import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import Papa from 'papaparse'; // Import papaparse for CSV generation

import Table from '../../shared/components/layout/Table';
import GridWrapper from '../../shared/components/layout/GridWrapper';
import Search from '../../shared/components/ui/Search';
import FileDownload from '../../../modules/shared/assets/svg/download.svg';
import NoDataFound from '../components/layout/NoDataFound';
import PaginationComponent from '../../shared/components/PaginationComponent';
import { transformData } from '../../super-admin/utils/transformData';
import { getAccountGstReports } from '../services/api/implementation/impl';
import Button from '../../shared/components/ui/Button';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

const GST = () => {
  const userId = Cookies.get('userId');
  /*
   * -------------------------------------------------
   * Component State
   * -------------------------------------------------
   */
  const { collapsed } = useSidebar();
  const [page, setPage] = useState(1);
  const [limit] = useState(10); // Items per page
  const [isDownloading, setIsDownloading] = useState(false); // State for download status
  const [dotCount, setDotCount] = useState(0); // State for dots animation

  /*
   * -------------------------------------------------
   * Fetching GST Reports from the API (Server-Side Pagination)
   * -------------------------------------------------
   */
  const {
    data: gstResponse,
    isFetching: isFetchingGST,
    isError,
    error
  } = useQuery({
    queryKey: ['gstReports', page, limit],
    queryFn: () => getAccountGstReports(page, limit),
    keepPreviousData: true
  });

  // If your API returns { data: [...], meta: {...} }
  const allGSTReports = gstResponse?.data || [];
  const totalItems = gstResponse?.meta?.total || 0;
  const totalPages = Math.ceil(totalItems / limit);

  /*
   * -------------------------------------------------
   * Transform the Data for Table
   * -------------------------------------------------
   */
  const gstDataFormat = [
    'id',
    'UnitName',
    'ProjectName',
    'BlockName',
    'CustomerName',
    'AmountPaid',
    'GST'
  ];

  const processedGSTReports = allGSTReports.map((report) => ({
    id: report.id || 'no-id',
    UnitName: report.unit_name || 'N/A',
    ProjectName: report.project_name || 'N/A',
    BlockName: report.block_name || 'N/A',
    CustomerName: report.customer_name || 'N/A',
    AmountPaid: report.amount_paid !== undefined ? `₹ ${report.amount_paid}` : '₹ 0',
    GST: report.gst_price !== undefined ? `₹ ${report.gst_price}` : '₹ 0'
  }));

  const gstData = processedGSTReports.map((row) => transformData(row, gstDataFormat));

  /*
   * -------------------------------------------------
   * Handlers
   * -------------------------------------------------
   */
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  /**
   * Handle Download CSV using papaparse
   */
  const handleDownload = async () => {
    if (isDownloading) return; // Prevent multiple clicks

    setIsDownloading(true);
    try {
      // Fetch all data with page=1 and limit=1000
      const response = await getAccountGstReports(1, 1000);
      const gstReports = response?.data || [];

      if (gstReports.length === 0) {
        alert('No data available to download.');
        return;
      }

      // Define CSV headers
      const headers = [
        'Unit Name',
        'Project Name',
        'Block Name',
        'Customer Name',
        'Amount Paid',
        'GST'
      ];

      // Map data to CSV rows as objects without the currency symbol
      const rows = gstReports.map((report) => ({
        'Unit Name': report.unit_name || 'N/A',
        'Project Name': report.project_name || 'N/A',
        'Block Name': report.block_name || 'N/A',
        'Customer Name': report.customer_name || 'N/A',
        'Amount Paid': report.amount_paid !== undefined ? report.amount_paid : 0,
        GST: report.gst_price !== undefined ? report.gst_price : 0
      }));

      // Convert to CSV using papaparse
      const csv = Papa.unparse({
        fields: headers,
        data: rows
      });

      // Create a Blob from the CSV content
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });

      // Create a temporary link to trigger the download
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.setAttribute('download', `gst_reports.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (downloadError) {
      console.error('Download failed:', downloadError);
      alert('Failed to download the file. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  /*
   * -------------------------------------------------
   * Dots Animation Effect
   * -------------------------------------------------
   */

  // Track page view
  useEffect(() => {
    mixpanel.track('G S T Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    let interval;
    if (isDownloading) {
      interval = setInterval(() => {
        setDotCount((prev) => (prev < 3 ? prev + 1 : 0));
      }, 500);
    } else {
      setDotCount(0);
    }

    return () => clearInterval(interval);
  }, [isDownloading]);

  const renderDownloadingText = () => {
    return `Downloading${'.'.repeat(dotCount)}`;
  };

  /*
   * -------------------------------------------------
   * Render
   * -------------------------------------------------
   */
  return (
    <div className="w-full flex flex-col relative z-5 bg-gray-100 pb-10 overflow-hidden">
      {/* Top Bar: Search, Download */}
      <div
        className={`transition-all duration-300 ease-in-out  flex items-center justify-between px-4 py-4 bg-white fixed top-[9.5rem] border-t-2 border-stone-100 ${collapsed ? 'left-[76px] right-0' : 'left-64 right-0'} shadow-sm`}>
        {/* Uncomment the Search component if needed */}
        {/* <Search placeholder="Search here..." /> */}
        <div></div>
        <div className="flex items-center gap-3 px-4">
          <button
            className={`relative flex items-center justify-center ${
              isDownloading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
            }`}
            onClick={handleDownload}
            disabled={isDownloading}
            aria-label="Download CSV">
            <img height={35} width={35} src={FileDownload} alt="File Download Icon" />
            {isDownloading && (
              <span className="absolute top-10 left-0 text-sm text-gray-700">
                {renderDownloadingText()}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* GST Reports Table */}
      <div className="mt-24 w-full h-full">
        {isFetchingGST ? (
          <div className="pt-12 text-center">
            <p>Loading...</p>
          </div>
        ) : isError ? (
          <div className="pt-12 text-center text-red-500">
            <p>Error: {error.message}</p>
          </div>
        ) : gstData && gstData.length > 0 ? (
          <GridWrapper width={100}>
            <div className="mt-[10rem] h-[28rem] relative">
              <Table
                headers={[
                  'Unit Name',
                  'Project Name',
                  'Block Name',
                  'Customer Name',
                  'Amount Paid',
                  'GST'
                ]}
                data={gstData}
                identifier="id"
                isViewEnable={false}
                isDeleteEnable={false}
                isEditEnable={false}
                isWrap={true}
              />

              <PaginationComponent
                page={page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                isFetching={isFetchingGST}
              />
            </div>
          </GridWrapper>
        ) : (
          <div className="pt-12">
            <NoDataFound disabledBtn screenHeight={true} />
          </div>
        )}
      </div>
    </div>
  );
};

export default GST;
